import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/shared/ui';
import { LOCALE_ENDONYMS, resolveSupportedLocale, SUPPORTED_LOCALES, type SupportedLocale } from '@/lib/i18nLocale';

export type LanguageSwitcherProps = {
  /** 메뉴 정렬 (Radix) */
  align?: 'start' | 'center' | 'end';
  /**
   * `icon`: 푸터·헤더 보조용(지구본만)
   * `labeled`: 스크린샷과 같이 "언어 선택" 라벨 + 드롭다운(현재 언어 강조)
   */
  variant?: 'icon' | 'labeled';
  className?: string;
  /** 드롭다운 콘텐츽 너비(라벨형에서 긴 스크립트용) */
  contentClassName?: string;
  /** 라벨형 버튼에 표시할 텍스트 */
  labelType?: 'select' | 'current';
  /** 라벨형 버튼의 시각 톤 */
  tone?: 'brand' | 'neutral';
};

/**
 * i18n 언어 전환 — `SUPPORTED_LOCALES`·`LOCALE_ENDONYMS`와 동기화.
 * DB가 아닌 UI 문자열은 `public/locales/{lng}/*.json` + `t()`로 번역됩니다.
 */
export default function LanguageSwitcher({
  align = 'end',
  variant = 'icon',
  className = '',
  contentClassName = '',
  labelType = 'select',
  tone = 'brand',
}: LanguageSwitcherProps): React.ReactElement {
  const { t, i18n } = useTranslation('common');
  const currentLocale = resolveSupportedLocale(i18n.language);
  const triggerLabel = labelType === 'current' ? LOCALE_ENDONYMS[currentLocale] : t('languageSelect');
  const labeledTriggerClassName = tone === 'neutral'
    ? 'border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50'
    : 'border-brand-500/40 bg-white text-gray-800 shadow-sm hover:bg-gray-50';
  const globeClassName = tone === 'neutral' ? 'text-gray-600' : 'text-brand-600';

  const handleLocaleChange = (locale: SupportedLocale) => {
    void i18n.changeLanguage(locale);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === 'labeled' ? (
          <button
            type="button"
            className={`inline-flex h-10 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium ${labeledTriggerClassName} ${className}`}
            aria-label={t('ariaLanguageMenu')}
          >
            <Globe size={18} className={`shrink-0 ${globeClassName}`} aria-hidden />
            <span className="max-w-[10rem] truncate sm:max-w-[14rem]">{triggerLabel}</span>
            <ChevronDown size={16} className="shrink-0 text-gray-500" aria-hidden />
          </button>
        ) : (
          <button
            type="button"
            className={`flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 ${className}`}
            aria-label={t('ariaLanguageMenu')}
          >
            <Globe size={18} />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className={contentClassName}>
        {SUPPORTED_LOCALES.map((locale) => (
          <DropdownMenuItem
            key={locale}
            onClick={() => handleLocaleChange(locale)}
            className={currentLocale === locale ? 'bg-gray-100 font-medium' : ''}
            dir="auto"
          >
            {LOCALE_ENDONYMS[locale]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
